import React, { useEffect, useState, useCallback } from "react";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  getOcrSummary,
  getOcrTrend,
  getOcrFieldBreakdown,
  OcrSummaryData,
  OcrTrendMonth,
  FieldBreakdownItem,
  ExtractionMethodRow,
} from "../../data/api/ocrQualityApi";
import { OcrAccuracyScorecard } from "../components/OcrAccuracyScorecard";
import { OcrAccuracyTrendChart } from "../components/OcrAccuracyTrendChart";
import { OcrFieldBreakdownChart } from "../components/OcrFieldBreakdownChart";
import { OcrExtractionMethodTable } from "../components/OcrExtractionMethodTable";

const ninetyDaysAgo = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
};

const today = (): string => new Date().toISOString().slice(0, 10);

export const OcrQualityDashboardPage: React.FC = () => {
  const [from, setFrom] = useState<string>(ninetyDaysAgo());
  const [to, setTo]     = useState<string>(today());

  const [summary,        setSummary]        = useState<OcrSummaryData | null>(null);
  const [trend,          setTrend]          = useState<OcrTrendMonth[]>([]);
  const [fieldBreakdown, setFieldBreakdown] = useState<FieldBreakdownItem[]>([]);
  const [extraction,     setExtraction]     = useState<ExtractionMethodRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string>("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [sumRes, trendRes, fieldRes] = await Promise.all([
        getOcrSummary(from, to),
        getOcrTrend(from, to),
        getOcrFieldBreakdown(from, to),
      ]);
      setSummary(sumRes.data);
      setTrend(trendRes.data);
      setFieldBreakdown(fieldRes.data.field_breakdown);
      setExtraction(fieldRes.data.extraction_comparison);
    } catch (e: any) {
      setError(e.message || "Failed to load OCR quality data.");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DefaultPageLayout>
      <div style={{ padding: "24px 32px", maxWidth: 1200 }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>
            OCR Quality Dashboard
          </h1>
          <p style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>
            AI extraction accuracy and human editing effort per document
          </p>
        </div>

        {/* Date range filter */}
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 28 }}>
          <label style={labelStyle}>
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={inputStyle}
            />
          </label>
          <button onClick={load} disabled={loading} style={buttonStyle}>
            {loading ? "Loading…" : "Apply"}
          </button>
        </div>

        {error && (
          <div style={errorBoxStyle}>{error}</div>
        )}

        {/* KPI Scorecards */}
        <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          <OcrAccuracyScorecard
            label="Overall OCR Accuracy"
            value={summary?.avg_overall_accuracy ?? null}
            greenThreshold={80}
            yellowThreshold={60}
          />
          <OcrAccuracyScorecard
            label="GL Code Accuracy"
            value={summary?.avg_gl_accuracy ?? null}
            greenThreshold={80}
            yellowThreshold={60}
          />
          <OcrAccuracyScorecard
            label="Zero-Edit Rate"
            value={summary?.zero_edit_rate_pct ?? null}
            greenThreshold={60}
            yellowThreshold={30}
          />
          <OcrAccuracyScorecard
            label="Avg Fields Edited / Doc"
            value={summary?.avg_fields_edited ?? null}
            unit=""
            higherIsBetter={false}
            greenThreshold={1}
            yellowThreshold={3}
          />
        </div>

        {summary && (
          <div style={{ marginBottom: 24, color: "#6b7280", fontSize: 13 }}>
            Based on <strong style={{ color: "#374151" }}>{summary.total_documents}</strong> posted documents in the selected period.
          </div>
        )}

        {/* Accuracy trend */}
        <div style={{ marginBottom: 24 }}>
          <OcrAccuracyTrendChart data={trend} />
        </div>

        {/* Field breakdown + extraction method */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
          <OcrFieldBreakdownChart data={fieldBreakdown} />
          <OcrExtractionMethodTable data={extraction} />
        </div>
      </div>
    </DefaultPageLayout>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 13,
  fontWeight: 500,
  color: "#374151",
};

const inputStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  fontSize: 13,
  color: "#111827",
  background: "#ffffff",
};

const buttonStyle: React.CSSProperties = {
  padding: "8px 20px",
  background: "#4f46e5",
  color: "#ffffff",
  border: "none",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const errorBoxStyle: React.CSSProperties = {
  background: "#fef2f2",
  border: "1px solid #fca5a5",
  borderRadius: 6,
  padding: "12px 16px",
  color: "#991b1b",
  fontSize: 14,
  marginBottom: 24,
};
